<template>
    <div class="l-date-picker">
        <div :class="['form-group', 'label-floating', wrapperClass, { 'is-focused': isFocused }]">
            <label :for="id" class="control-label">{{ label }}</label>
            <div v-if="readonly">
                <l-input-text
                    :value="timeInputDisplay"
                    label=""
                    type="text"
                    hide-help-block
                    data-test-id="meeting-date-readoinly"
                    :readonly="true"
                />
            </div>
            <div v-else>
                <datepicker
                    :id="id"
                    :value="timeInput"
                    :language="lang"
                    :placeholder="placeholder"
                    :highlighted="highlighted"
                    :input-class="`form-control ${inputClass}`"
                    :bootstrap-styling="true"
                    :typeable="typeable"
                    :disabled="disabled"
                    :disabled-dates="disabledDates"
                    :wrapper-class="isFullWidth ? 'tw-max-w-full' : ''"
                    @input="onDateChange"
                    @focus="onFocus"
                    @blur="onBlur"
                ></datepicker>
            </div>
        </div>
    </div>
</template>

<script>
import { mapGetters } from "vuex";

import generateDefaultId from "@/utils/generate-default-id";

import Datepicker from "@sum.cumo/vue-datepicker";

// /!\ @sum.cumo/vue-datepicker has its own logic for handling i18n /!\ \\
import {
    en,
    fr,
    de,
    es,
    nl,
    ja,
    pt,
    it,
    ko,
    zh_CN,
    sv,
} from "@sum.cumo/vue-datepicker/dist/locale";
import LInputText from "@/components/LInputText";

export default {
    components: {
        Datepicker,
        LInputText,
    },
    props: {
        label: {
            type: String,
            default: "",
        },
        placeholder: {
            type: String,
            default: "",
        },
        value: {
            type: [Date, String],
            default: null,
        },
        onDateChange: {
            type: Function,
            default: () => {},
        },
        id: {
            type: String,
            default: () => generateDefaultId(),
        },
        highlighted: {
            type: Object,
            default: () => ({ dates: [new Date()] }),
        },
        wrapperClass: {
            type: String,
            default: "",
        },
        inputClass: {
            type: String,
            default: "override-read-only",
        },
        typeable: {
            type: Boolean,
            default: true,
        },
        disabled: {
            type: Boolean,
            default: false,
        },
        disabledDates: {
            type: Object,
            default: () => ({}),
        },
        readonly: {
            type: Boolean,
            default: false,
        },
        isFullWidth: {
            type: Boolean,
            default: false,
        },
        isUTC: {
            type: Boolean,
            default: false,
        },
    },
    data() {
        return {
            isFocused: false,
        };
    },
    computed: {
        ...mapGetters({
            locale: "users/current/locale",
        }),
        lang() {
            switch (this.locale) {
                case "de":
                    return de;
                case "en":
                    return en;
                case "fr":
                    return fr;
                case "es":
                    return es;
                case "pt":
                    return pt;
                case "it":
                    return it;
                case "sv":
                    return sv;
                case "nl":
                    return nl;
                case "ja":
                    return ja;
                case "ko":
                    return ko;
                case "zh_CN":
                    return zh_CN;
                default:
                    return en;
            }
        },
        timeInput() {
            if (this.isUTC && this.value) {
                return this.value.toISOString();
            } else {
                return this.value;
            }
        },
        timeInputDisplay() {
            if (this.isUTC && this.value) {
                return this.$moment.utc(this.value).format("DD MMM YYYY");
            } else {
                return this.$moment(this.value).format("DD MMM YYYY");
            }
        },
    },
    methods: {
        onFocus() {
            this.isFocused = true;
        },
        onBlur() {
            this.isFocused = false;
        },
    },
};
</script>

<style scoped>
@import "~@sum.cumo/vue-datepicker/dist/Datepicker.css";

.tw-max-w-full >>> .input-group {
    width: 100%;
}

/**
 *  In vuejs-datepicker, the input field is read-only.
 *  Yet, we want the input to look like it can still be modified,
 *  thus adding an override class to reset the background-color.
 */
.l-date-picker >>> .override-read-only {
    background-color: #f9f9f9 !important;
}

.form-group {
    /* extra padding to be consistent with LInputText */
    padding-bottom: 20px;
}

/** Some defaults for usage in tailwind layouts */
.tw.form-group {
    line-height: 1;
}

.tw .form-group,
.tw .form-group .control-label {
    margin: 0;
    padding: 0;
}

.tw .form-group .form-control {
    margin: 0;
}

.tw label.control-label {
    top: 0 !important;
    position: unset !important;
    line-height: 1 !important;
    margin-bottom: 4px !important;
}
</style>
